import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  BodyText,
  Button,
  Flex,
  Heading,
  LoadingAnimation,
  Stack,
} from "@knowtex-ai/components";
import { usePylonSsoUrl } from "../../hooks/usePylonSsoUrl";
import {
  closePendingPylonTab,
  openPendingPylonTab,
  openPylonSsoUrl,
  toTrustedPylonSsoUrl,
} from "../../utils/pylonKnowledgeBase";

const INITIAL_STATUS =
  "Opening Knowtex Resources. We are preparing your secure knowledge base access.";
const RETRY_STATUS =
  "Retrying Knowtex Resources. We are preparing your secure knowledge base access.";
const READY_STATUS = "Knowtex Resources is ready in a new tab.";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export default function ResourcesLaunch(): ReactElement {
  const navigate = useNavigate();
  const { mutateAsync, reset } = usePylonSsoUrl();
  const [hasLaunchError, setHasLaunchError] = useState(false);
  const [ssoUrl, setSsoUrl] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState(INITIAL_STATUS);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
  const hasStartedRef = useRef(false);
  const isMountedRef = useRef(false);
  const mainRef = useRef<HTMLElement>(null);

  const startLaunch = useCallback(async () => {
    if (hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;
    const pendingTab = openPendingPylonTab();

    try {
      const { url } = await mutateAsync();
      if (!isMountedRef.current) {
        closePendingPylonTab(pendingTab);
        return;
      }

      const trustedUrl = toTrustedPylonSsoUrl(url);
      if (!trustedUrl) {
        throw new Error("Invalid Pylon SSO URL");
      }

      openPylonSsoUrl(trustedUrl, pendingTab);
      setSsoUrl(trustedUrl);
      setStatusMessage(READY_STATUS);
    } catch {
      closePendingPylonTab(pendingTab);
      if (isMountedRef.current) {
        setHasLaunchError(true);
        setSsoUrl(null);
      }
      hasStartedRef.current = false;
    }
  }, [mutateAsync]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const syncPreference = () => setPrefersReducedMotion(mediaQuery.matches);

    syncPreference();
    mediaQuery.addEventListener("change", syncPreference);
    return () => mediaQuery.removeEventListener("change", syncPreference);
  }, []);

  useEffect(() => {
    if (!hasLaunchError) {
      mainRef.current?.focus({ preventScroll: true });
    }
  }, [hasLaunchError, ssoUrl]);

  useEffect(() => {
    isMountedRef.current = true;
    void startLaunch();

    return () => {
      isMountedRef.current = false;
    };
  }, [startLaunch]);

  const retryLaunch = () => {
    setStatusMessage(RETRY_STATUS);
    setHasLaunchError(false);
    setSsoUrl(null);
    reset();
    hasStartedRef.current = false;
    void startLaunch();
  };

  const isPreparing = !hasLaunchError && !ssoUrl;
  const pageTitle = ssoUrl ? "Resources – Knowtex" : "Opening Resources – Knowtex";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <div
        id="resources-launch-status"
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {hasLaunchError ? "" : statusMessage}
      </div>
      <main
        ref={mainRef}
        id="main-content"
        tabIndex={-1}
        aria-busy={isPreparing}
        aria-labelledby="resources-launch-heading"
        data-testid="resources-page"
        className="relative flex min-h-screen w-[100dvw] items-center justify-center overflow-x-hidden bg-background1 px-4 py-12"
      >
        <section className="relative z-10 w-full max-w-lg rounded-xl bg-white px-8 py-10 text-center shadow-drop-shadow tablet:px-12">
          {hasLaunchError ? (
            <Stack gap={24}>
              <div id="resources-launch-heading">
                <Heading as="h1" size="large" className="text-accent1">
                  Resources are temporarily unavailable
                </Heading>
              </div>
              <Banner variant="error" showIcon>
                We could not finish preparing your secure Resources access. Retry the connection or
                return to the Knowtex dashboard.
              </Banner>
              <BodyText muted>Your Knowtex session remains available.</BodyText>
              <Flex justifyContent="center" gap={12} wrap="wrap">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate("/dashboard", { replace: true })}
                >
                  Return to dashboard
                </Button>
                <Button type="button" variant="primary-alt" onClick={retryLaunch}>
                  Retry
                </Button>
              </Flex>
            </Stack>
          ) : ssoUrl ? (
            <Stack gap={24}>
              <div id="resources-launch-heading">
                <Heading as="h1" size="large" className="text-accent1">
                  Knowtex Resources is ready
                </Heading>
              </div>
              <BodyText muted>
                The knowledge base opened in a new tab. If nothing appeared, open it below. Your
                Knowtex session stays on this page.
              </BodyText>
              <Flex justifyContent="center" gap={12} wrap="wrap">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate("/dashboard", { replace: true })}
                >
                  Return to dashboard
                </Button>
                <a
                  href={ssoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="resources-open-link"
                  className="inline-flex items-center justify-center rounded-lg bg-[#783BFF] px-5 py-2.5 font-semibold text-white no-underline hover:opacity-90"
                >
                  Open Knowtex Resources
                </a>
              </Flex>
            </Stack>
          ) : (
            <Stack gap={20} className="items-center">
              {!prefersReducedMotion ? (
                <div aria-hidden="true" data-testid="resources-loading-animation">
                  <LoadingAnimation size={48} color="#783BFF" />
                </div>
              ) : null}
              <Stack gap={8}>
                <div id="resources-launch-heading">
                  <Heading as="h1" size="large" className="text-accent1">
                    Opening Knowtex Resources
                  </Heading>
                </div>
                <BodyText muted>We are preparing your secure knowledge base access.</BodyText>
              </Stack>
            </Stack>
          )}
        </section>
      </main>
    </>
  );
}
